import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Button from '@cloudscape-design/components/button'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Box from '@cloudscape-design/components/box'
import ProgressBar from '@cloudscape-design/components/progress-bar'
import Badge from '@cloudscape-design/components/badge'
import { useNavigate } from 'react-router'

interface AgentGoal {
  id: string
  goalType: string
  targetMetric: string
  targetValue: number
  currentValue: number
  deadline: string
  status: string
}

interface AgentPersona {
  id: string
  name: string
}

interface Props {
  clientId: string
  goal: AgentGoal | null
  persona: AgentPersona | null
  nextAutoRunAt: string | null
  lastAutoRunAt: string | null
  loading: boolean
}

function formatLastRun(iso: string): string {
  const past = new Date(iso)
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterdayStart = new Date(todayStart.getTime() - 86400000)

  const timeOpts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }
  const timeStr = new Intl.DateTimeFormat('en-US', timeOpts).format(past)

  if (past >= todayStart) return `Today at ${timeStr}`
  if (past >= yesterdayStart) return `Yesterday at ${timeStr}`
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', ...timeOpts }).format(past)
}

function formatNextRun(iso: string): string {
  const next = new Date(iso)
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const tomorrowStart = new Date(todayStart.getTime() + 86400000)
  const dayAfterStart = new Date(tomorrowStart.getTime() + 86400000)

  const timeOpts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }
  const timeStr = new Intl.DateTimeFormat('en-US', timeOpts).format(next)

  if (next >= todayStart && next < tomorrowStart) return `Today at ${timeStr}`
  if (next >= tomorrowStart && next < dayAfterStart) return `Tomorrow at ${timeStr}`
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', ...timeOpts }).format(next)
}

const GOAL_LABELS: Record<string, string> = {
  grow_referrals: 'Grow referrals',
  increase_engagement: 'Increase engagement',
  expand_provider_network: 'Expand provider network',
  launch_service: 'Launch service',
}

export function AgentStatusSection({ clientId, goal, persona, nextAutoRunAt, lastAutoRunAt, loading }: Props) {
  const navigate = useNavigate()

  const headerActions = nextAutoRunAt ? (
    <Badge color="blue">Next run: {formatNextRun(nextAutoRunAt)}</Badge>
  ) : undefined

  return (
    <Container header={<Header variant="h2" actions={headerActions}>Agent Status</Header>}>
      <SpaceBetween size="m">
        {loading ? (
          <StatusIndicator type="loading">Loading…</StatusIndicator>
        ) : goal ? (
          <>
            <StatusIndicator type="success">
              Active{persona ? ` — ${persona.name}` : ''}
            </StatusIndicator>
            <SpaceBetween size="xs">
              <Box variant="p">
                <Box variant="span" fontWeight="bold">Goal: </Box>
                {GOAL_LABELS[goal.goalType] ?? goal.goalType}
              </Box>
              <ProgressBar
                value={goal.targetValue > 0 ? Math.round((goal.currentValue / goal.targetValue) * 100) : 0}
                additionalInfo={`${goal.currentValue} / ${goal.targetValue} ${goal.targetMetric.replace(/_/g, ' ')}`}
                description={`Deadline: ${goal.deadline}`}
              />
              {lastAutoRunAt && (
                <Box variant="small" color="text-body-secondary">
                  Last ran: {formatLastRun(lastAutoRunAt)}
                </Box>
              )}
            </SpaceBetween>
            <Button variant="link" onClick={() => navigate(`/workspace/clients/${clientId}/agent/setup`)}>
              Reconfigure
            </Button>
          </>
        ) : (
          <>
            <StatusIndicator type="stopped">Not configured</StatusIndicator>
            <Button onClick={() => navigate(`/workspace/clients/${clientId}/agent/setup`)}>
              Set up agent
            </Button>
          </>
        )}
      </SpaceBetween>
    </Container>
  )
}
